import React, { useState } from "react";
import { useAccumulate } from "@/components/context/AccumulateContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { searchEntities, type BusinessEntity } from "@/components/api/nzbnApi";
import { SafeLink } from "@/components/ui/SafeLink";;


const QuoteTablePage: React.FC = () => {
    const { quoteItems, setQuoteItems } = useAccumulate();

    // Contact form state
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [company, setCompany] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [businessEntities, setBusinessEntities] = useState<BusinessEntity[]>([]);
    const [selectedBusinessEntity, setSelectedBusinessEntity] = useState<BusinessEntity | null>(null);
    const [showManualCompany, setShowManualCompany] = useState(false);

    // NZBN lookup
    const handleBusinessEntitySearch = async (searchTerm: string) => {
        setCompany(searchTerm);
        setShowManualCompany(false);
        if (!searchTerm) {
            setBusinessEntities([]);
            return;
        }
        try {
            const res = await searchEntities(searchTerm, 'businessName');
            setBusinessEntities(res);
        } catch (error) {
            setBusinessEntities([]);
        }
    };

    const handleBusinessEntityClick = (entity: BusinessEntity) => {
        setSelectedBusinessEntity(entity);
        setCompany(entity.label);
        setBusinessEntities([]);
        setShowManualCompany(false);
    };

    const handleManualCompany = () => {
        setSelectedBusinessEntity(null);
        setBusinessEntities([]);
        setShowManualCompany(true);
    };

    const handleClear = () => setQuoteItems([]);
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Add your submit logic here (e.g. send to API)
        alert(
            `Quote request submitted!\n` +
            `First Name: ${firstName}\nLast Name: ${lastName}\nCompany: ${company}\nEmail: ${email}\nMessage: ${message}\nItems: ${quoteItems.length}`
        );
    };

    return (
        <div className="bg-gray-50 w-screen min-h-screen py-8 px-4 lg:px-12 xl:px-24 ">
            <div className="w-full flex flex-col items-center justify-center">
                <h1 className="text-2xl sm:text-3xl font-bold text-harvey-red text-center">Request a Quote</h1>
                <div className="flex flex-col lg:flex-row gap-6 items-center justify-center lg:items-start lg:mx-auto py-8 w-full">
                    {/* Contact Form */}
                    <form className="flex-1 w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-4xl bg-white rounded shadow p-4 sm:p-6"
                        onSubmit={handleSubmit}
                        id="quote-contact-form">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                            <div>
                                <Label className="block text-sm font-semibold mb-2 text-gray-700">First Name</Label>
                                <Input
                                    type="text"
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-harvey-red"
                                    value={firstName}
                                    onChange={e => setFirstName(e.target.value)}
                                    required
                                />
                            </div>
                            <div>
                                <Label className="block text-sm font-semibold mb-2 text-gray-700">Last Name</Label>
                                <Input
                                    type="text"
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-harvey-red"
                                    value={lastName}
                                    onChange={e => setLastName(e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                        <div className="mb-4 relative">
                            <Label className="block text-sm font-semibold mb-2 text-gray-700">Company Name (NZBN Lookup)</Label>
                            {!showManualCompany && (
                                <>
                                    <Input
                                        type="text"
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-harvey-red"
                                        value={company}
                                        onChange={e => handleBusinessEntitySearch(e.target.value)}
                                        required={!showManualCompany}
                                        placeholder="Enter business name or NZBN..."
                                    />
                                    {businessEntities.length > 0 && (
                                        <div className="absolute z-50 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto mt-1 w-full">
                                            {businessEntities.map((entity: BusinessEntity) => (
                                                <div
                                                    key={entity.value}
                                                    className="p-2 cursor-pointer hover:bg-gray-100 border-b last:border-b-0 transition-colors"
                                                    onClick={() => handleBusinessEntityClick(entity)}
                                                >
                                                    <span className="text-sm font-medium text-black truncate">{entity.label}</span>
                                                    <span className="text-xs text-gray-600 ml-2">NZBN: {entity.info.nzbn}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className="mt-2 text-xs text-harvey-red underline"
                                        onClick={handleManualCompany}
                                    >
                                        Can't find your company? Enter manually
                                    </button>
                                </>
                            )}
                            {showManualCompany && (
                                <Input
                                    type="text"
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-harvey-red"
                                    value={company}
                                    onChange={e => setCompany(e.target.value)}
                                    required
                                    placeholder="Enter your company name"
                                />
                            )}
                        </div>
                        <div className="mb-4">
                            <Label className="block text-sm font-semibold mb-2 text-gray-700">Email</Label>
                            <Input
                                type="email"
                                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-harvey-red"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <div className="mb-4">
                            <Label className="block text-sm font-semibold mb-2 text-gray-700">Message</Label>
                            <textarea
                                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-harvey-red"
                                value={message}
                                onChange={e => setMessage(e.target.value)}
                                rows={3}
                                placeholder="Add any notes or requirements..."
                                required
                            />
                        </div>
                        {/* <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        className="bg-gray-200 text-gray-800 px-5 py-2 rounded hover:bg-gray-300 font-semibold"
                        onClick={handleClear}
                    >
                        Clear All
                    </Button>
                    <Button
                        type="submit"
                        variant="red"
                        className="bg-harvey-red text-white px-5 py-2 rounded hover:bg-harvey-red/90 font-semibold"
                        disabled={quoteItems.length === 0}
                    >
                        Submit Quote Request
                    </Button>
                </div> */}
                    </form>

                    {/* No Items Message or table */}
                    <div className="flex-1 w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-4xl">
                        <div className="flex flex-col justify-between items-center h-full">
                            {quoteItems.length === 0 ? (
                                <div className="flex flex-col items-center justify-center w-full h-full py-12">
                                    <p className="text-center text-gray-500 mb-6 text-lg font-medium">
                                        No items in your quote list.
                                    </p>
                                    <SafeLink
                                        to="/all-products"
                                        className="px-6 py-2 rounded-full font-semibold bg-harvey-red text-white hover:bg-harvey-red/90 transition"
                                    >
                                        Browse Products
                                    </SafeLink>
                                </div>

                            ) : (
                                <div className="overflow-x-auto w-full">
                                    <table className="min-w-full bg-white rounded shadow">
                                        <thead>
                                            <tr className="bg-harvey-red/90 text-white">
                                                <th className="py-3 px-4 text-left">Item Name</th>
                                                <th className="py-3 px-4 text-left">Quantity</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {quoteItems.map((item, idx) => (
                                                <tr key={idx} className="border-b last:border-b-0">
                                                    <td className="py-3 px-4 text-black">{item.name}</td>
                                                    <td className="py-3 px-4 text-black">{item.quantity}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                        </div>

                    </div>

                </div>
                {quoteItems.length > 0 && (
                    <div className="">

                        <div className="w-full flex  gap-3  max-w-md sm:max-w-lg md:max-w-xl lg:max-w-4xl">
                            <button
                                className="bg-gray-200 text-gray-800 px-5 py-2 rounded hover:bg-gray-300 font-semibold"
                                onClick={handleClear}
                            >
                                Clear All
                            </button>
                            <button
                                className="bg-harvey-red text-white px-5 py-2 rounded hover:bg-harvey-red/90 font-semibold"
                                onClick={handleSubmit}
                            >
                                Submit Quote Request
                            </button>
                        </div>

                    </div>
                )}

            </div>
        </div>
    );
};

export default QuoteTablePage;